
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { Zap, CheckCircle, FileText, TrendingUp, Leaf, Calculator, BarChart3, Lightbulb } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function EnerjiVerimliligi() {
  const { t } = useTranslation();

  const energyServices = [
    {
      icon: FileText,
      title: t('services.enerji.certificate'),
      description: 'Binaların enerji performansını gösteren resmi belge düzenleme hizmeti',
      features: [
        'Enerji performans hesaplaması',
        'Bina enerji sınıflandırması',
        'Yasal zorunluluk karşılama',
        'Emlak değer artışı',
        '10 yıl geçerlilik süresi'
      ],
      image: 'https://public.readdy.ai/ai/img_res/6357b76ae9fe908dc03feaedc7fcd1c7.jpg'
    },
    {
      icon: Calculator,
      title: t('services.enerji.study'),
      description: 'Detaylı enerji analizi ve tasarruf potansiyelinin belirlenmesi',
      features: [
        'Enerji tüketim analizi',
        'Tasarruf potansiyeli hesaplama',
        'Yatırım geri dönüş analizi',
        'Teknik çözüm önerileri',
        'Maliyet-fayda analizi'
      ],
      image: 'https://readdy.ai/api/search-image?query=Energy%20efficiency%20study%20with%20engineer%20analyzing%20energy%20consumption%20data%2C%20charts%20and%20graphs%2C%20energy%20audit%20equipment%2C%20industrial%20facility%20background&width=600&height=400&seq=20&orientation=landscape'
    },
    {
      icon: Lightbulb,
      title: t('services.enerji.consulting'),
      description: 'Enerji verimliliği konularında uzman danışmanlık hizmetleri',
      features: [
        'Enerji yönetim sistemi kurulumu',
        'Verimlilik projelerinin planlanması',
        'Teknoloji seçimi danışmanlığı',
        'Teşvik ve destek başvuruları',
        'Sürekli iyileştirme programları'
      ],
      image: 'https://readdy.ai/api/search-image?query=Energy%20efficiency%20consulting%20with%20professional%20consultant%20presenting%20energy%20solutions%2C%20renewable%20energy%20systems%2C%20solar%20panels%2C%20energy%20management%20dashboard&width=600&height=400&seq=21&orientation=landscape'
    },
    {
      icon: Leaf,
      title: t('services.enerji.zerowaste'),
      description: 'Sıfır atık hedefi doğrultusunda süreç yönetimi ve danışmanlık',
      features: [
        'Atık azaltma stratejileri',
        'Geri dönüşüm süreçleri',
        'Sürdürülebilirlik planlaması',
        'Çevresel etki azaltma',
        'Yasal uyumluluk sağlama'
      ],
      image: 'https://readdy.ai/api/search-image?query=Zero%20waste%20process%20management%20with%20recycling%20systems%2C%20waste%20reduction%20strategies%2C%20sustainable%20practices%2C%20green%20technology%2C%20environmental%20compliance&width=600&height=400&seq=22&orientation=landscape'
    }
  ];

  const energyProcess = [
    {
      step: '01',
      title: 'Ön Değerlendirme',
      description: 'Mevcut enerji durumunun analizi ve ihtiyaç belirleme'
    },
    {
      step: '02',
      title: 'Sahada İnceleme',
      description: 'Detaylı enerji ölçümleri ve sistem analizi'
    },
    {
      step: '03',
      title: 'Hesaplama ve Analiz',
      description: 'Enerji performans hesaplamaları ve değerlendirme'
    },
    {
      step: '04',
      title: 'Rapor Hazırlama',
      description: 'Detaylı enerji raporu ve önerilerin sunumu'
    },
    {
      step: '05',
      title: 'Belge Düzenleme',
      description: 'Resmi belgelerin hazırlanması ve teslimi'
    },
    {
      step: '06',
      title: 'Takip ve Destek',
      description: 'Uygulama sonrası izleme ve sürekli destek'
    }
  ];

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Maliyet Tasarrufu',
      description: 'Enerji faturalarında %20-40 oranında azalma'
    },
    {
      icon: Leaf,
      title: 'Çevresel Fayda',
      description: 'Karbon ayak izinde önemli azalma'
    },
    {
      icon: BarChart3,
      title: 'Verimlilik Artışı',
      description: 'Sistem performansında iyileştirme'
    },
    {
      icon: FileText,
      title: 'Yasal Uyumluluk',
      description: 'Enerji mevzuatına tam uyum'
    }
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section 
          className="relative py-24 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://readdy.ai/api/search-image?query=Energy%20efficiency%20services%20with%20modern%20building%20featuring%20solar%20panels%2C%20energy%20monitoring%20systems%2C%20green%20technology%2C%20sustainable%20architecture%2C%20professional%20energy%20assessment&width=1920&height=800&seq=23&orientation=landscape')`
          }}
        >
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center text-white">
              <h1 className="text-5xl font-bold mb-6">{t('services.enerji.title')}</h1>
              <p className="text-xl leading-relaxed">
                {t('services.enerji.description')}
              </p>
            </div>
          </div>
        </section>

        {/* Main Services */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Enerji Verimliliği Hizmetlerimiz</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Sürdürülebilir enerji çözümleri ile maliyetlerinizi azaltın ve çevreye katkı sağlayın
              </p>
            </div>

            <div className="space-y-16">
              {energyServices.map((service, index) => (
                <div key={index} className={`flex flex-col lg:flex-row items-center gap-12 ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                  <div className="lg:w-1/2">
                    <img 
                      src={service.image} 
                      alt={service.title}
                      className="w-full h-80 object-cover rounded-lg shadow-lg"
                    />
                  </div>
                  <div className="lg:w-1/2">
                    <div className="flex items-center mb-4">
                      <service.icon className="w-8 h-8 text-red-600 mr-3" />
                      <h3 className="text-3xl font-bold text-gray-800">{service.title}</h3>
                    </div>
                    <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                      {service.description}
                    </p>
                    <ul className="space-y-3 mb-6">
                      {service.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <button className="bg-red-600 text-white px-8 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium whitespace-nowrap cursor-pointer">
                      Hizmet Al
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Enerji Verimliliğinin Faydaları</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Enerji verimliliği yatırımlarının getirdiği avantajlar
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-lg text-center hover:shadow-xl transition-shadow">
                  <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-8 h-8 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-gray-800 mb-4">Çalışma Sürecimiz</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Sistematik yaklaşımımızla enerji verimliliği projelerinizi yönetiyoruz
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {energyProcess.map((step, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-lg text-center hover:shadow-xl transition-shadow">
                  <div className="w-16 h-16 bg-red-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Statistics */}
        <section className="py-20 bg-red-600 text-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">Başarı İstatistiklerimiz</h2>
              <p className="text-xl max-w-3xl mx-auto">
                Enerji verimliliği projelerinde elde ettiğimiz sonuçlar
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">500+</div>
                <p className="text-white/90">Tamamlanan Proje</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">%35</div>
                <p className="text-white/90">Ortalama Enerji Tasarrufu</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">2.5M</div>
                <p className="text-white/90">Analiz Edilen m² Alan</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">%98</div>
                <p className="text-white/90">Müşteri Memnuniyeti</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-4xl font-bold text-gray-800 mb-6">Enerji Tasarrufuna Başlayın</h2>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Uzman ekibimiz ile enerji maliyetlerinizi azaltın ve sürdürülebilir bir gelecek için 
              adım atın. Ücretsiz ön değerlendirme için hemen iletişime geçin.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-red-600 text-white px-8 py-4 rounded-lg font-bold hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer">
                Ücretsiz Değerlendirme
              </button>
              <button className="border-2 border-red-600 text-red-600 px-8 py-4 rounded-lg font-bold hover:bg-red-600 hover:text-white transition-colors whitespace-nowrap cursor-pointer">
                Bilgi Al
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
